import { useState, useEffect, useRef } from 'react';
import Notification from '../../notification/notification';
import { PlusIcon } from '@heroicons/react/20/solid';
import categoryApi from '../../../services/categoryApi';
import CategoryUpsertModal from './category-upsert-modal';
import ModalDelete from '../../modal-delete/modal-delete';
import TWConfirmModal from '../../ui/tw-confirm-modal';
import CategoryCard from './category-card';
import { CategoryListSkeleton } from '../../skeleton/skeleton';
import { capitalizeWords, capitalizeSentence } from '../../../utils/baseUtils';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';

export default function CategoryList({
  selected,
  setSelected,
  needsReviewByCategory,
  preferredCategoryId = null,
  deepLinkNonce,
  onPreferredConsumed,
}) {
  // Tracks which menuid the currently-loaded categoryList was fetched for,
  // so the deepLinkNonce effect below (which may fire in the same commit as
  // a menuid change, before the corresponding fetch resolves) never applies
  // a preferred selection against a stale, wrong-menu list. The parent's
  // post-fetch call is what actually handles that case once the fresh list
  // lands.
  const lastFetchedMenuIdRef = useRef(null);
  const [deletedItem, setDeletedItem] = useState({ name: '', id: '' });
  const [notf, setNotf] = useState({
    display: false,
    type: 'error',
    message: '',
  });
  const [errors, setErrors] = useState({
    name: false,
    displayorder: false,
  });
  const [categoryForm, setCategoryForm] = useState({
    id: '',
    name: '',
    displayorder: 0,
    menuid: selected.menuid,
    description: '',
  });
  const [categoryDialogs, setCategoryDialogs] = useState({
    categoryUpsert: false,
    categoryDelete: false,
  });
  const [categoryList, setCategoryList] = useState([]);
  const [loading, setLoading] = useState({
    categoryLoading: true,
    upsertLoading: false,
    deleteLoading: false,
  });
  // Applies the deep-linked preferred category against a fetched list.
  // Called (a) right after every fetch resolves and (b) from the
  // deepLinkNonce effect against the already-fetched list, so a queued
  // in-page link (needs-review modal) can be re-attempted without a refetch.
  // Consumption (onPreferredConsumed) fires whenever we actually attempt —
  // matched or not — silent fallback for a stale/bad id.
  const tryApplyPreferred = (list) => {
    if (!preferredCategoryId) return;
    const match = list.find((c) => c.id === preferredCategoryId);
    if (match) {
      setSelected((prev) => ({
        ...prev,
        category: match,
        product: {},
        productid: '',
        itemid: '',
        optiongroupid: '',
        optionid: '',
      }));
    }
    onPreferredConsumed?.('categoryid');
  };

  const getCategoryList = async (method) => {
    try {
      setLoading((prev) => ({ ...prev, categoryLoading: true }));

      const query = `menuid=${selected.menuid}`;
      const list = await categoryApi.list(query);
      lastFetchedMenuIdRef.current = selected.menuid;

      if (list.data.data.length > 0) {
        setCategoryList(list.data.data);
        tryApplyPreferred(list.data.data);
        setSelected((prev) => ({
          ...prev,
          category:
            method?.method === 'update' && prev?.category?.id
              ? list.data.data.find((x) => x.id === prev.category.id) || {}
              : prev.category || {},
          ...(method?.method === 'update' &&
          prev?.category?.id &&
          !list.data.data.find((x) => x.id === prev.category.id)
            ? {
                product: {},
                itemid: '',
                optiongroupid: '',
                optionid: '',
              }
            : {}),
          menuStoreIds: list.data.data[0]?.menu?.menustores?.map(
            (x) => x.storeid,
          ),
        }));
      } else {
        setCategoryList([]);
        tryApplyPreferred([]);
        setSelected((prev) => ({
          ...prev,
          category: {},
          itemid: '',
          product: {},
          menuStoreIds: [],
        }));
      }
    } catch (error) {
      setNotf((prev) => ({
        ...prev,
        message: error,
        display: true,
      }));
    } finally {
      setLoading((prev) => ({ ...prev, categoryLoading: false }));
    }
  };

  const onClickAddCategory = () => {
    setCategoryForm({
      id: '',
      name: '',
      description: '',
      displayorder:
        categoryList.length > 0
          ? categoryList[categoryList.length - 1].displayorder + 1
          : 1,
      menuid: selected.menuid,
    });

    setCategoryDialogs((prev) => ({ ...prev, categoryUpsert: true }));
  };

  const onFormChange = (val, key) => {
    setCategoryForm((prev) => ({ ...prev, [key]: val }));
  };

  const onClickDeleteCategory = (category) => {
    setDeletedItem(category);
    setCategoryDialogs((prev) => ({ ...prev, categoryDelete: true }));
  };

  const onConfirmDelete = async () => {
    try {
      setLoading((prev) => ({ ...prev, deleteLoading: true }));
      await categoryApi.deleteById(deletedItem.id);
      getCategoryList();
    } catch (error) {
    } finally {
      setCategoryDialogs((prev) => ({
        ...prev,
        categoryDelete: false,
        categoryUpsert: false,
      }));
      setLoading((prev) => ({ ...prev, deleteLoading: false }));
    }
  };

  const [duplicateTarget, setDuplicateTarget] = useState(null);
  const [duplicateLoading, setDuplicateLoading] = useState(false);

  const onClickDuplicateCategory = (category) => {
    setDuplicateTarget(category);
  };

  const runDuplicateCategory = async () => {
    if (!duplicateTarget) return;
    setDuplicateLoading(true);
    try {
      const res = await categoryApi.duplicate(duplicateTarget.id);
      const newName = res?.data?.data?.name || res?.data?.name || 'category';
      setNotf({
        display: true,
        type: 'success',
        message: `Duplicated as "${newName}"`,
      });
      await getCategoryList();
    } catch (e) {
      setNotf({
        display: true,
        type: 'error',
        message:
          e?.response?.data?.Message ||
          e?.response?.data ||
          'Duplicate failed',
      });
    } finally {
      setDuplicateLoading(false);
      setDuplicateTarget(null);
    }
  };

  const onClickEditCategory = (val) => {
    setCategoryForm({
      id: val.id,
      name: val.name,
      displayorder: val.displayorder,
      menuid: val.menuid,
      description: val.description,
    });

    setCategoryDialogs((prev) => ({ ...prev, categoryUpsert: true }));
  };

  const onCloseUpsert = () => {
    setErrors((prev) => ({ ...prev, displayorder: false, name: false }));
    setCategoryDialogs((prev) => ({ ...prev, categoryUpsert: false }));
  };

  const onFormSubmit = async () => {
    if (!categoryForm.name || categoryForm.displayorder === '') {
      setErrors((prev) => ({
        ...prev,
        name: !categoryForm.name ? true : false,
        displayorder: categoryForm.displayorder === '' ? true : false,
      }));
      return;
    } else {
      setErrors((prev) => ({ ...prev, name: false, displayorder: false }));
    }
    try {
      setLoading((prev) => ({ ...prev, upsertLoading: true }));
      const body = { ...categoryForm };
      // Capitalize category name and description before saving
      if (body.name) {
        body.name = capitalizeWords(body.name.trim());
      }
      if (body.description) {
        body.description = capitalizeSentence(body.description.trim());
      }
      const res = await categoryApi.upsert(body);
      if (res.data.data) {
        setCategoryDialogs((prev) => ({ ...prev, categoryUpsert: false }));
      }
    } catch (error) {
    } finally {
      setLoading((prev) => ({ ...prev, upsertLoading: false }));
      await getCategoryList({ method: categoryForm.id ? 'update' : 'create' });
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 5 },
    }),
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = categoryList.findIndex((item) => item.id === active.id);
    const newIndex = categoryList.findIndex((item) => item.id === over.id);
    const reordered = arrayMove(categoryList, oldIndex, newIndex);

    setCategoryList(reordered);

    try {
      await categoryApi.reorder({
        items: reordered.map((item, index) => ({
          id: item.id,
          displayorder: index,
        })),
      });
    } catch (error) {
      console.error('Error reordering categories:', error);
      await getCategoryList();
    }
  };

  useEffect(() => {
    getCategoryList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected.menuid, selected.isCategoryFetch]);

  // Re-attempt a queued in-page deep link (needs-review modal) against the
  // list we already have. Guarded on lastFetchedMenuIdRef so a nonce bump
  // that arrives bundled with a menuid change (openProductDeepLink sets both
  // in one go) doesn't apply against the previous menu's stale list — the
  // effect above's post-fetch call handles that case once the new list lands.
  useEffect(() => {
    if (lastFetchedMenuIdRef.current !== selected.menuid) return;
    tryApplyPreferred(categoryList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkNonce]);
  return (
    <>
      {loading.categoryLoading ? (
        <CategoryListSkeleton />
      ) : (
        <nav className="">
          {selected.category.id !== '' && (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
              modifiers={[restrictToVerticalAxis]}
            >
              <SortableContext
                items={categoryList.map((c) => c.id)}
                strategy={verticalListSortingStrategy}
              >
                <div>
                  {categoryList.map((category, i) => (
                    <CategoryCard
                      key={category.id}
                      index={i}
                      id={category.id}
                      selected={selected}
                      setSelected={setSelected}
                      onClickEditCategory={onClickEditCategory}
                      onClickDuplicateCategory={onClickDuplicateCategory}
                      category={category}
                      needsReviewCount={needsReviewByCategory?.[category.id] || 0}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
          <div
            onClick={() => onClickAddCategory()}
            className="mx-3 my-2 flex h-[5.25rem] cursor-pointer flex-col items-center justify-center rounded-md border border-dashed border-blue-300 bg-blue-50/40 p-4 text-sm font-medium text-blue-600 transition hover:bg-blue-50 dark:border-slate-500 dark:bg-slate-700/40 dark:text-slate-200 dark:hover:bg-slate-600"
          >
            <button
              type="button"
              className="rounded-full bg-blue-600 p-1 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
            >
              <PlusIcon className="h-5 w-5" aria-hidden="true" />
            </button>
            <span className="mt-1 truncate font-semibold text-[0.925rem]">
              Add Category
            </span>
          </div>
        </nav>
      )}
      <CategoryUpsertModal
        show={categoryDialogs.categoryUpsert}
        loading={loading.upsertLoading}
        categoryForm={categoryForm}
        errors={errors}
        onCloseUpsert={onCloseUpsert}
        onFormChange={onFormChange}
        onFormSubmit={onFormSubmit}
        onClickDeleteCategory={onClickDeleteCategory}
      />

      <ModalDelete
        title="Category"
        text={`Are you sure you want to delete "${deletedItem.name}"? All associated data will be permanently removed.`}
        loading={loading.deleteLoading}
        open={categoryDialogs.categoryDelete}
        onCloseRequest={() => {
          setCategoryDialogs((prev) => ({ ...prev, categoryDelete: false }));
        }}
        onOk={onConfirmDelete}
        item={deletedItem}
      />
      <TWConfirmModal
        open={!!duplicateTarget}
        tone="info"
        title={`Duplicate "${duplicateTarget?.name || ''}"?`}
        description={
          duplicateTarget
            ? 'A copy will be created and appended to the end of this menu, along with its products and their items.'
            : ''
        }
        confirmLabel="Duplicate"
        loading={duplicateLoading}
        onConfirm={runDuplicateCategory}
        onClose={() => !duplicateLoading && setDuplicateTarget(null)}
      />
      <Notification
        display={notf.display}
        type={notf.type}
        message={notf.message}
        onClose={() => setNotf((prev) => ({ ...prev, display: false }))}
      />
    </>
  );
}
